import React, { useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import { format, isToday, isYesterday } from 'date-fns';
import { usePatientInteractions } from '../../hooks/queries/usePatientInteractions';
import type { PatientInteraction } from '../../services/patientInteractionsService';
import { useCurrentPatientStore } from '../../stores/currentPatientStore';

/**
 * Topbar patient interaction history (GP-1508).
 *
 * "Any time we interact with an order, we want to store that patient in a
 * history log … accessible as a menu from the top bar … and it should be user
 * level." The rows are recorded server-side, so this menu is a pure read: open
 * it and it refetches, showing your own recent patients in the active pharmacy,
 * each once at their newest interaction (PRX-88, collapsed server-side),
 * newest first, grouped by day.
 *
 * A row navigates to the patient's Patient 360 profile. When the interaction was
 * on an order, the order number next to it is its own link to that order — two
 * separate destinations, so neither click is a guess.
 */

interface InteractionDay {
  key: string;
  label: string;
  rows: PatientInteraction[];
}

/** "Today" / "Yesterday" / "Wed, 27 Aug" — one header per calendar day. */
const dayLabel = (date: Date): string => {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEE, d MMM');
};

/**
 * Group rows into calendar days, preserving the newest-first order the API
 * returned. Grouping is done on the LOCAL calendar day (date-fns `format`),
 * never on an ISO/UTC slice — after 8pm a UTC day is tomorrow's date.
 */
export const groupInteractionsByDay = (rows: PatientInteraction[]): InteractionDay[] => {
  const days: InteractionDay[] = [];

  rows.forEach((row) => {
    const date = new Date(row.createdAt);
    if (Number.isNaN(date.getTime())) return;

    const key = format(date, 'yyyy-MM-dd');
    const existing = days.find((day) => day.key === key);
    if (existing) {
      existing.rows.push(row);
    } else {
      days.push({ key, label: dayLabel(date), rows: [row] });
    }
  });

  return days;
};

export const PatientInteractionsMenu: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // PRX-85: the patient the current page/order/dialog is about, if any —
  // shown bold on the trigger itself so staff always know whose chart they're
  // in, without changing what the trigger opens.
  const current = useCurrentPatientStore((s) => s.patient);

  // Only fetch while the menu is open. The query going from disabled to enabled
  // is what triggers the fetch, and `staleTime: 0` makes every re-open a fresh
  // read — so the list reflects the work just done without a second manual
  // refetch racing the first request.
  const { data, isLoading, isError } = usePatientInteractions({ enabled: open });

  const days = useMemo(() => groupInteractionsByDay(data ?? []), [data]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const goToPatient = (row: PatientInteraction) => {
    handleClose();
    navigate(`/patients/${row.compositePatientId}`);
  };

  const goToOrder = (event: React.MouseEvent, row: PatientInteraction) => {
    event.stopPropagation();
    if (!row.orderId) return;
    handleClose();
    navigate(`/orders/${row.pharmacyId}/${row.orderId}`);
  };

  return (
    <>
      {current ? (
        <Tooltip title="Recent patients">
          <Button
            onClick={handleOpen}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            aria-label={`Recent patients — ${current.displayName}`}
            startIcon={<HistoryIcon />}
            color="inherit"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.05rem',
              maxWidth: 320,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'white',
              bgcolor: alpha(theme.palette.common.white, 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.2) },
            }}
          >
            {current.displayName}
          </Button>
        </Tooltip>
      ) : (
        <Tooltip title="Recent patients">
          <IconButton
            aria-label="Recent patients"
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleOpen}
            sx={{
              color: 'white',
              bgcolor: alpha(theme.palette.common.white, 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.2) },
            }}
          >
            <HistoryIcon />
          </IconButton>
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 380,
              maxHeight: 480,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Patient history
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Patients you worked on in this pharmacy
          </Typography>
        </Box>
        <Divider />

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!isLoading && isError && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Could not load patient history
            </Typography>
          </Box>
        )}

        {!isLoading && !isError && days.length === 0 && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No patient interactions yet
            </Typography>
          </Box>
        )}

        {!isLoading &&
          !isError &&
          days.flatMap((day) => [
            <Typography
              key={`heading-${day.key}`}
              variant="caption"
              component="div"
              color="text.secondary"
              sx={{ px: 2, pt: 1.5, pb: 0.5, fontWeight: 600 }}
            >
              {day.label}
            </Typography>,
            ...day.rows.map((row) => (
              <MenuItem
                key={row.id}
                onClick={() => goToPatient(row)}
                sx={{ py: 1, alignItems: 'flex-start' }}
              >
                <Box sx={{ display: 'flex', gap: 1, width: '100%', minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                  >
                    {format(new Date(row.createdAt), 'HH:mm')}
                  </Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {row.patientName || `Patient ${row.patientId}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {row.actionLabel}
                      {row.orderNumber && (
                        <>
                          {' '}
                          <Box
                            component="span"
                            role="link"
                            tabIndex={0}
                            onClick={(event: React.MouseEvent) => goToOrder(event, row)}
                            onKeyDown={(event: React.KeyboardEvent) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                goToOrder(event as unknown as React.MouseEvent, row);
                              }
                            }}
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                          >
                            ({row.orderNumber})
                          </Box>
                        </>
                      )}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            )),
          ])}
      </Menu>
    </>
  );
};

export default PatientInteractionsMenu;
